import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  User,
  Calendar,
  TrendingUp,
  Brain,
  Award,
  FileText,
  BarChart3
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ipvService from '../../services/ipvService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';

/**
 * Componente para visualizar resultados del Test IPV
 * Muestra interpretación completa por escalas
 */
const RevisionExamenIpv = () => {
  const { examenId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [evaluacion, setEvaluacion] = useState(null);

  useEffect(() => {
    cargarResultados();
  }, [examenId]);

  const cargarResultados = async () => {
    try {
      setLoading(true);
      
      // Obtener el examen
      const examenData = await ipvService.obtenerExamen(examenId);
      
      if (!examenData.ipvEvaluacionId) {
        toast.error('Este examen no tiene evaluación asociada');
        return;
      }

      // Obtener evaluación
      const evaluacionData = await ipvService.obtenerEvaluacion(examenData.ipvEvaluacionId);
      setEvaluacion(evaluacionData);

      // Obtener resultado interpretado
      const resultadoData = await ipvService.obtenerResultadoInterpretado(examenData.ipvEvaluacionId);
      setResultado(resultadoData);
      
    } catch (error) {
      console.error('Error al cargar resultados:', error);
      toast.error('Error al cargar los resultados del examen');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (!resultado) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Card>
          <div className="p-8 text-center">
            <Brain className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-neutral-800 mb-2">
              No se encontraron resultados
            </h2>
            <p className="text-neutral-600 mb-4">
              Este examen aún no ha sido evaluado o no existe.
            </p>
            <Button onClick={() => navigate(-1)}>
              Volver
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const escalas = [
    { key: 'dgv', icon: Award, color: 'purple' },
    { key: 'receptividad', icon: TrendingUp, color: 'blue' },
    { key: 'agresividad', icon: TrendingUp, color: 'red' },
    { key: 'comprension', icon: Brain, color: 'green' },
    { key: 'adaptabilidad', icon: TrendingUp, color: 'cyan' },
    { key: 'controlSiMismo', icon: TrendingUp, color: 'indigo' },
    { key: 'toleranciaFrustracion', icon: TrendingUp, color: 'orange' },
    { key: 'combatividad', icon: TrendingUp, color: 'pink' },
    { key: 'dominancia', icon: TrendingUp, color: 'violet' },
    { key: 'seguridad', icon: TrendingUp, color: 'teal' },
    { key: 'actividad', icon: TrendingUp, color: 'amber' },
    { key: 'sociabilidad', icon: TrendingUp, color: 'lime' }
  ];

  const getNivelColor = (nivel) => {
    const colores = {
      'ALTO': 'bg-green-100 text-green-800 border-green-300',
      'PROMEDIO': 'bg-blue-100 text-blue-800 border-blue-300',
      'BAJO': 'bg-amber-100 text-amber-800 border-amber-300'
    };
    return colores[nivel] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-4 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>

          <Card className="border-l-4 border-l-purple-600">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-10 h-10 text-purple-600" />
                <div>
                  <h1 className="text-3xl font-bold text-neutral-800">
                    Resultados del Test IPV
                  </h1>
                  <p className="text-neutral-600">
                    Inventario de Personalidad para Vendedores
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="flex items-center gap-2 text-neutral-700">
                  <User className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-neutral-500">Postulante</p>
                    <p className="font-semibold">{resultado.nombrePostulante || `ID: ${resultado.postulanteId}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-neutral-700">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-neutral-500">Fecha</p>
                    <p className="font-semibold">
                      {resultado.fechaEvaluacion ? new Date(resultado.fechaEvaluacion).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-neutral-700">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-xs text-neutral-500">Evaluación</p>
                    <p className="font-semibold">#{resultado.evaluacionId}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Resultado DGV destacado */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <Award className="w-12 h-12 text-purple-600 flex-shrink-0" />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-purple-900 mb-2">
                    {resultado.dgv.nombreEscala}
                  </h2>
                  <div className="flex items-center gap-4 mb-3">
                    <Badge className={`${getNivelColor(resultado.dgv.nivel)} text-lg px-4 py-1`}>
                      {resultado.dgv.nivel}
                    </Badge>
                    <div className="text-sm text-neutral-700">
                      <span className="font-semibold">Decatipo:</span> {resultado.dgv.decatipo}
                    </div>
                    <div className="text-sm text-neutral-700">
                      <span className="font-semibold">PT:</span> {resultado.dgv.puntuacionTransformada}
                    </div>
                    <div className="text-sm text-neutral-700">
                      <span className="font-semibold">PD:</span> {resultado.dgv.puntuacionDirecta}
                    </div>
                  </div>
                  <p className="text-neutral-700 leading-relaxed">
                    {resultado.dgv.interpretacion}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Escalas individuales */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-600" />
            Escalas Individuales
          </h2>

          {escalas.slice(1).map((escala, index) => {
            const data = resultado[escala.key];
            if (!data) return null;

            const Icon = escala.icon;

            return (
              <motion.div
                key={escala.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <Icon className={`w-8 h-8 text-${escala.color}-600 flex-shrink-0 mt-1`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-bold text-neutral-800">
                            {data.nombreEscala}
                          </h3>
                          <Badge className={`${getNivelColor(data.nivel)} px-3 py-1`}>
                            {data.nivel}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-6 mb-3 text-sm text-neutral-600">
                          <div>
                            <span className="font-semibold">Código:</span> {data.codigoEscala}
                          </div>
                          <div>
                            <span className="font-semibold">Decatipo:</span> {data.decatipo}
                          </div>
                          <div>
                            <span className="font-semibold">PT:</span> {data.puntuacionTransformada}
                          </div>
                          <div>
                            <span className="font-semibold">PD:</span> {data.puntuacionDirecta}
                          </div>
                        </div>

                        {/* Barra de progreso visual */}
                        <div className="mb-3">
                          <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                            <div
                              className={`bg-${escala.color}-600 h-full rounded-full transition-all`}
                              style={{ width: `${(data.decatipo / 10) * 100}%` }}
                            />
                          </div>
                        </div>

                        <p className="text-neutral-700 text-sm leading-relaxed">
                          {data.interpretacion}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Botón de exportar/imprimir */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex justify-center gap-4"
        >
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="gap-2"
          >
            <FileText className="w-4 h-4" />
            Imprimir Resultados
          </Button>
          <Button
            onClick={() => navigate(-1)}
            className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al listado
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default RevisionExamenIpv;
